// LICENSE_CODE ZON
'use strict'; /*jslint browser:true, es6:true, node:true, react:true*/
import React, {ReactNode, useEffect, useMemo, useRef, Fragment} from 'react';
import cn from 'classnames';
import Icon_chevron_down from '../../img/hola2023/icons/16/chevron_down.svg';
import {Cards, i_card, i_cls_obj} from '../cards';
import {use_theme} from '../../settings_ctx';
import {Link} from '../link';
import styles from './nav_menu.module.less';
import {use_countries} from '../forms/use_countries';
import {Button} from '../../lib/button';
import {use_premium_page} from '../premium/use_premium_page';
import {format_price} from 'zcountry';

interface i_menu_item_props {
    id: string;
    to: string;
    title: string;
    content?: ReactNode;
    is_plus?: boolean;
    has_ext?: boolean;
    cls?: string;
}
export const Menu_item = (props: i_menu_item_props)=>{
    const {id, to, title, content, is_plus, has_ext, cls} = props;
    const {theme} = use_theme({def: 'hola2023'});
    const ref = useRef<HTMLDivElement>();
    let content_cls = props.title=='Get Hola' ?
        `${styles.menu_content} ${styles.get_hola}` : styles.menu_content;
    content_cls += is_plus ? ' '+styles.has_plus : '';
    content_cls += !has_ext ? ' '+styles.no_ext : '';
    content_cls += cls ? ' '+cls : '';
    useEffect(()=>{
        if (!ref.current)
            return;
        const calc_shift = ()=>{
            const shift = ref.current.getBoundingClientRect().left;
            ref.current.style.setProperty('--left', `${shift}px`);
        };
        calc_shift();
        window.addEventListener('resize', calc_shift);
        return ()=>{
            window.removeEventListener('resize', calc_shift);
        };
    }, [ref.current]);
    return <div className={cn(styles.menu_item, styles[id])} ref={ref}>
      <div className={styles.menu_link}>
        <Link to={to} className={cn(theme.P, theme.p3)}
          data-clickid={`nav_${id}`}>{title}</Link>
        {content && <Icon_chevron_down className={styles.link_icon}/>}
      </div>
      {content && <div className={content_cls}>{content}</div>}
    </div>;
};

interface i_menu_cards_props {
    cards: i_card[];
    cls?: i_cls_obj;
    children?: ReactNode;
}
export const Menu_cards = (props: i_menu_cards_props)=>{
    const {cards = [], cls, children} = props;
    return <div className={cn(styles.menu_cards, cls.cards_wrapper
      && cls.cards_wrapper)}>
      <Cards cards={cards} cls={cls}
        spacer={<div className={cn(styles.card_spacer, cls.cards_spacer
        && cls.cards_spacer)}/>}/>
      {children}
    </div>;
};

const Support_menu = ()=>{
    const {theme} = use_theme({def: 'hola2023'});
    const cards: i_card[] = useMemo(()=>{
        const ids = ['help', 'troubleshooting', 'report'];
        const data = {
            help: {
                title: 'Help Center',
                text: 'The Hola support team',
                to: 'https://support.hola.org/',
            },
            troubleshooting: {
                title: 'Troubleshooting',
                text: 'Hola\'s step by step guide',
                to: 'https://support.hola.org/collections/'
                    +'9875490-troubleshooting',
            },
            report: {
                title: 'Report a Problem',
                text: 'Get in touch with Hola Support',
                to: 'https://hola.org/premium_support',
            },
        };
        return ids.map(id=>({
            id,
            img: `hola2023/navbar/support_${id}.svg`,
            clickid: `nav_support_${id}`,
            ...data[id],
        }));
    }, []);
    const cls: i_cls_obj = {
        container: styles.cards,
        card: styles.card_wide,
        title: cn(theme.u3, styles.no_wrap),
        text: cn(theme.p3, theme.reg),
        img: styles.flex_img,
    };
    return <Menu_cards cards={cards} cls={cls}/>;
};

const Get_hola_menu = ()=>{
    const {theme} = use_theme({def: 'hola2023'});
    const cards: i_card[] = useMemo(()=>{
        const ids = ['ext', 'mobile', 'desktop', 'tv', 'others'];
        const data = {
            ext: {
                title: 'Extensions',
                text: 'For Chrome, Opera and Microsoft Edge',
                to: '/products/extension',
            },
            mobile: {
                title: 'Phone Apps',
                text: 'For iOS, Samsung and Huawei ',
                to: 'https://hola.org/products/mobile',
            },
            desktop: {
                title: 'Desktop Apps',
                text: 'For Windows and Mac',
                to: 'https://hola.org/products/desktop',
            },
            tv: {
                title: 'TV Apps',
                text: 'For Fire TV, LG TV, Apple TV and Smart TV',
                to: 'https://hola.org/products/tv',
            },
            others: {
                title: 'More Products',
                text: 'Gaming apps, routers and smart DNS ',
                to: 'https://hola.org/products/console',
            },
        };
        return ids.map(id=>({
            id,
            img: `hola2023/products_discover_${id}.svg`,
            clickid: `nav_get_hola_${id}`,
            ...data[id],
        }));
    }, []);
    const cls: i_cls_obj = {
        container: styles.cards,
        card: styles.card_wide,
        title: cn(theme.u3, styles.no_wrap),
        text: cn(theme.p3, theme.reg),
        img: styles.flex_img,
    };
    return <Menu_cards cards={cards} cls={cls}/>;
};

const Vpn_locations = ()=>{
    const {theme} = use_theme({def: 'hola2023'});
    const {countries} = use_countries('f32', cn(styles.flag_wrap,
        theme.xs, theme.P), styles.flag_icon, null, null, true);
    const cards: i_card[] = useMemo(()=>{
        const ids = ['US', 'GB', 'MX'];
        const data = {
            US: {
                title: 'United States',
                to: 'https://hola.org/vpn/us',
            },
            GB: {
                title: 'United Kingdom',
                to: 'https://hola.org/vpn/gb',
            },
            MX: {
                title: 'Mexico',
                to: 'https://hola.org/vpn/mexico',
            },
        };
        return ids.map(id=>({
            id,
            img: countries.find(c=>c.value==id)?.rendered_item || '',
            clickid: `nav_get_hola_${id}`,
            ...data[id],
        }));
    }, []);
    const cls: i_cls_obj = {
        card: styles.country_card,
        container: styles.country_cards_container,
        cards_wrapper: styles.country_cards_wrapper,
        cards_spacer: styles.country_cards_spacer,
        title: cn(theme.u3, styles.no_wrap),
        text: cn(theme.p3, theme.reg),
        img: styles.country_img,
    };
    return <div className={styles.location_nav_menu}>
        <Menu_cards cards={cards} cls={cls}/>
        <Button caption={<>Check all 190+ locations <span
          className={styles.arrow}>&rsaquo;</span> </>}
          to="https://hola.org/vpn" size={'xs'} color="transparent"/>
    </div>;
};
const Plans = ()=>{
    const {theme} = use_theme({def: 'hola2023'});
    const {opt: {plans, currency}} = use_premium_page({from_bext: false,
        from_nav_bar: true});
    return <div className={styles.plans_nav_menu}>
      {plans && Array.isArray(plans) && plans.filter(p=>p.id!=='free')
        .map((p, i)=>{
        const link = `https://hola.org/premium?ref=navbar`+
            `&plan=${p.period.replace(' ', '').toLowerCase()}`;
        const best = 'josh_3y';
        const plan = p.currency && p.currency[currency.toLowerCase()]||p;
        return <Fragment key={p.id}>
          <a className={styles.plan_container} href={link}>
          {p.id==best && <div className={styles.best}>&nbsp;</div>}
          <div className={cn(styles.plan, p.id==best && styles.plan_best)}>
            <div className={cn(styles.plan_wrapper)}>
            <div className={cn(theme.p2, theme.med, theme.color_n700,
              styles.plan_header)}>
              {p.short_name}
              {p.id==best && <span>best value</span>}</div>
            <div className={cn(theme.H5, theme.color_n700, styles.plan_month)}>
              {format_price(currency, plan.month_price)}
              <span> / month</span></div>
            <div className={styles.plan_occurrence}>
              {plan.regular_price && plan.regular_price!==plan.price &&
              <div className={styles.regular_price_wrapper}>
                <span className={styles.plan_regular_price_crossed}>
                  {format_price(currency, plan.regular_price)}</span>
                <span className={styles.plan_regular_price}> {format_price(
                  currency, plan.price)}</span>
              </div>}
              billed every {plan.every}</div>
            </div>
            <Button caption="Try Premium"
              size={'xs'} color={p.id==best ? 'black': 'transparent'}/>
            </div>
            </a>
            {i!==plans.filter(pl=>pl.id!=='free').length - 1 &&
              <div className={styles.plan_separator}>&nbsp;</div>}
        </Fragment>; })}
    </div>;
};

const Hola_menu = ()=>{
    const {theme} = use_theme({def: 'hola2023'});
    const cards: i_card[] = useMemo(()=>{
        const ids = ['about', 'blog', 'media'];
        const data = {
            about: {
                title: 'About Hola',
                text: 'Wanna know more about us?',
                to: 'https://hola.org/about',
            },
            blog: {
                title: 'Blog',
                text: 'The latest news from Hola!',
                to: 'https://hola.org/blog',
            },
            media: {
                title: 'Media',
                text: 'What the press is saying',
                to: 'https://hola.org/media',
            },
        };
        return ids.map(id=>({
            id,
            img: `hola2023/navbar/hola_${id}.svg`,
            clickid: `nav_hola_${id}`,
            ...data[id],
        }));
    }, []);
    const cls: i_cls_obj = {
        container: styles.cards,
        card: styles.card_wide,
        title: cn(theme.u3, styles.no_wrap),
        text: cn(theme.p3, theme.reg),
        img: styles.flex_img,
    };
    return <Menu_cards cards={cards} cls={cls}/>;
};

interface i_nav_menu_props {
    is_plus: boolean;
    visible: boolean;
    has_ext?: boolean;
}
export const Nav_menu = (props: i_nav_menu_props)=>{
    const {is_plus, visible = true, has_ext} = props;
    return <div className={cn(styles.nav_menu, visible && styles.visible)}>
      {!is_plus && <Menu_item id="premium" content={<Plans />}
        cls={styles.content_full}
        to="https://hola.org/premium?ref=navbar" title="Premium"/>}
      <Menu_item id="get_hola" to="https://hola.org/products" title="Get Hola"
        is_plus={is_plus} has_ext={has_ext} content={<Get_hola_menu/>}/>
      <Menu_item id="get_hola" to="https://hola.org/vpn" title="VPN Locations"
        is_plus={is_plus} has_ext={has_ext} content={<Vpn_locations/>}/>
      <Menu_item id="support" to="https://hola.org/premium_support"
        title="Support" content={<Support_menu/>}/>
      <Menu_item id="about" to="https://hola.org/about" title="About"
        content={<Hola_menu/>}/>
    </div>;
};
